import { useEffect, useMemo, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import {
  useCircleTexture,
  useMousePosition,
  makeDriftOnBeforeCompile,
} from "./particleHooks";
import { readParams } from "../dragonParams";
import {
  getDragonGeometry,
  startDragonLoad,
  subscribeDragonGeometry,
} from "../dragonAsset";
import { swirlFade, swirlProgress, swirlStarted } from "../swirlPolicy";

/* The dragon's entrance: the particles start scattered into a rotating cloud
   and spiral onto their settled surface positions, so the figure resolves out
   of the swirl. It replaces the old load-keyed opacity fade, which on a fast
   connection played out entirely behind the intro overlay and was never seen.

   `revealed` is a prop rather than a `useRevealed()` call because this renders
   inside the r3f reconciler, on the far side of the <Canvas> boundary; see
   ParticleField, which reads the context on the DOM side and passes it down. */
function DragonPoints({ revealed }) {
  const pointsRef = useRef();
  const circleTexture = useCircleTexture();
  const mouse = useMousePosition();
  const mouseOffset = useRef({ x: 0, y: 0 });
  /* Take any cached buffers synchronously. On a cold visit this is null, so the
     component renders nothing until the in-flight load publishes its result.
     On a warm remount after that load completed, the first frame can draw the
     dragon without waiting a tick for the subscription effect. */
  const [geometryData, setGeometryData] = useState(() => getDragonGeometry());
  const paramsRef = useRef(readParams());
  const t = useRef(0);
  const driftShader = useRef(null);
  const baseDriftShader = useRef(null);
  const swirlStart = useRef(null);

  /* Materials are constructed here rather than declared as <pointsMaterial>
     elements: onBeforeCompile only runs when the program is compiled, and a
     prop applied after that first compile never reaches the shader. Building
     the material with the hook already attached guarantees it is in place. */
  const detailMaterial = useMemo(() => {
    const params = paramsRef.current;
    const material = new THREE.PointsMaterial({
      size: params.size,
      map: circleTexture,
      alphaTest: params.alphaTest,
      vertexColors: true,
      transparent: true,
      opacity: params.opacity,
      sizeAttenuation: true,
      depthWrite: params.depthWrite,
    });
    material.onBeforeCompile = makeDriftOnBeforeCompile(
      driftShader,
      params.float,
      params.floatSpeed,
      {
        distance: params.disperseDistance,
        speed: params.emitSpeed,
        size: params.emitSize,
      },
      {
        radius: params.swirlRadius,
        turns: params.swirlTurns,
        scatter: params.swirlScatter,
        lift: params.swirlLift,
        stagger: params.swirlStagger,
      },
    );
    // keep this program distinct from any stock PointsMaterial in the cache
    material.customProgramCacheKey = () => "dragon-points-emit";
    return material;
  }, [circleTexture]);

  const baseMaterial = useMemo(() => {
    const params = paramsRef.current;
    const material = new THREE.PointsMaterial({
      size: params.baseSize,
      map: circleTexture,
      vertexColors: true,
      transparent: true,
      opacity: params.baseOpacity,
      sizeAttenuation: true,
      depthWrite: false,
      depthTest: false,
    });
    material.onBeforeCompile = makeDriftOnBeforeCompile(
      baseDriftShader,
      params.float,
      params.floatSpeed,
    );
    material.customProgramCacheKey = () => "dragon-points-base";
    return material;
  }, [circleTexture]);

  /* The load normally started long before this mount, back when the page was
     first opened. Calling again is free and covers the one case it did not:
     arriving at the landing by client-side navigation from /blog. */
  useEffect(() => {
    startDragonLoad();
    return subscribeDragonGeometry(setGeometryData);
  }, []);

  useFrame((state, delta) => {
    if (!pointsRef.current) return;
    const params = paramsRef.current;
    t.current += delta * 0.3;
    if (driftShader.current) driftShader.current.uniforms.uTime.value += delta;
    if (baseDriftShader.current)
      baseDriftShader.current.uniforms.uTime.value += delta;

    /* The swirl clock starts on the LATER of the curtain lifting and the
       geometry landing, never on load. Before that it stays pinned at fully
       scattered and fully faded out, which is what lets the layer keep warming
       up the program and the buffers behind the overlay while drawing nothing.

       One uniform per frame is the entire CPU cost of the animation: where each
       of the 450,000 particles sits at this instant is the vertex shader's
       problem, and rewriting the position buffer from here would cost more per
       frame than everything else the landing does put together. */
    if (swirlStart.current === null && swirlStarted(revealed, !!geometryData)) {
      swirlStart.current = state.clock.elapsedTime;
    }
    /* Negative before the hold-off has expired, and both dials read that as
       "not started yet" - fully scattered, fully faded out. */
    const elapsedMs =
      swirlStart.current === null
        ? -Infinity
        : (state.clock.elapsedTime - swirlStart.current) * 1000 -
          params.swirlDelayMs;
    const scatter = swirlProgress(elapsedMs, params.swirlMs, params.swirlEase);
    const fade = swirlFade(elapsedMs, params.swirlFadeMs);
    if (driftShader.current) {
      driftShader.current.uniforms.uSwirl.value = scatter;
      driftShader.current.uniforms.uSwirlFade.value = fade;
    }
    /* The crisp layer's own opacity is fixed; its entrance rides the shader
       above. The underlay carries no swirl, and no alphaTest either, so a plain
       opacity fade is still the right thing there. */
    baseMaterial.opacity = params.baseOpacity * fade;

    mouseOffset.current.x += (mouse.current.x - mouseOffset.current.x) * 0.03;
    mouseOffset.current.y += (mouse.current.y - mouseOffset.current.y) * 0.03;

    // vertical cursor movement tips the model about X, horizontal about Y
    pointsRef.current.rotation.x =
      -THREE.MathUtils.degToRad(params.pitch) -
      mouseOffset.current.y * params.tilt;
    pointsRef.current.rotation.y =
      THREE.MathUtils.degToRad(params.yaw) +
      mouseOffset.current.x * params.tilt +
      Math.sin(t.current * 0.15) * params.spin;

    pointsRef.current.position.x =
      params.x + mouseOffset.current.x * params.parallax;
    pointsRef.current.position.y =
      params.y + mouseOffset.current.y * params.parallax * 0.7;
  });

  if (!geometryData) return null;

  const params = paramsRef.current;

  return (
    <group ref={pointsRef} position={[params.x, params.y, params.z]}>
      {geometryData.baseCount > 0 && (
        /* Drawn first and kept out of the depth buffer: the crisp layer writes
           depth, so a base that tested against it would be punched away exactly
           where the two overlap. */
        <points renderOrder={-1}>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              count={geometryData.baseCount}
              array={geometryData.basePositions}
              itemSize={3}
            />
            <bufferAttribute
              attach="attributes-color"
              count={geometryData.baseCount}
              array={geometryData.baseColors}
              itemSize={3}
            />
            <bufferAttribute
              attach="attributes-aPhase"
              count={geometryData.baseCount}
              array={geometryData.basePhases}
              itemSize={1}
            />
            <bufferAttribute
              attach="attributes-aDrift"
              count={geometryData.baseCount}
              array={geometryData.baseDrifts}
              itemSize={1}
            />
            <bufferAttribute
              attach="attributes-aEmit"
              count={geometryData.baseCount}
              array={geometryData.baseEmits}
              itemSize={1}
            />
            <bufferAttribute
              attach="attributes-aNormal"
              count={geometryData.baseCount}
              array={geometryData.baseNormals}
              itemSize={3}
            />
          </bufferGeometry>
          <primitive object={baseMaterial} attach="material" />
        </points>
      )}
      <points>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={geometryData.count}
            array={geometryData.positions}
            itemSize={3}
          />
          <bufferAttribute
            attach="attributes-color"
            count={geometryData.count}
            array={geometryData.colors}
            itemSize={3}
          />
          <bufferAttribute
            attach="attributes-aPhase"
            count={geometryData.count}
            array={geometryData.phases}
            itemSize={1}
          />
          <bufferAttribute
            attach="attributes-aDrift"
            count={geometryData.count}
            array={geometryData.drifts}
            itemSize={1}
          />
          <bufferAttribute
            attach="attributes-aEmit"
            count={geometryData.count}
            array={geometryData.emits}
            itemSize={1}
          />
          <bufferAttribute
            attach="attributes-aNormal"
            count={geometryData.count}
            array={geometryData.normals}
            itemSize={3}
          />
        </bufferGeometry>
        <primitive object={detailMaterial} attach="material" />
      </points>
    </group>
  );
}

export default DragonPoints;
